import React, { useState, MouseEvent } from 'react'
import { useTagsStore } from '@/store/tagsStore'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from '@/components/ui/button'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tag } from '@/types/tag'

interface TagSelectProps {
  value: string[]
  onChange: (value: string[]) => void
}

export function TagSelect({ value, onChange }: TagSelectProps) {
  const { tags, isLoading } = useTagsStore()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedTags = tags.filter(tag => value.includes(tag.id))

  const handleSelect = (tagId: string) => {
    if (value.includes(tagId)) {
      onChange(value.filter(id => id !== tagId))
    } else {
      onChange([...value, tagId])
    }
  }

  const filteredTags = searchQuery === ''
    ? tags
    : tags.filter(tag => 
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      )

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between bg-white dark:bg-[#1E293B] border border-[#2F2F2F]/10 dark:border-[#333840] hover:border-[#439476] dark:hover:border-[#439476] transition-colors w-full h-11 rounded-lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-[#439476]" />
                <span>Loading tags...</span>
              </div>
            ) : selectedTags.length > 0 
              ? `${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'} selected`
              : "Select tags..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-[#439476]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-full p-0 bg-white dark:bg-[#1E293B] border border-[#2F2F2F]/10 dark:border-[#333840] shadow-lg rounded-lg"
          align="start"
          side="bottom"
          sideOffset={4}
          style={{ zIndex: 99999 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Command className="bg-transparent" shouldFilter={false}>
            <CommandInput 
              placeholder="Search tags..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="border-none focus:ring-0 h-11"
            />
            <CommandEmpty className="py-2 text-center text-sm text-[#6B7280] dark:text-[#A3A3A3]">
              {isLoading ? "Loading tags..." : "No tags found."}
            </CommandEmpty>
            <CommandGroup className="max-h-[200px] overflow-auto p-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-[#439476]" />
                </div>
              ) : filteredTags.map((tag) => (
                <CommandItem
                  key={tag.id}
                  onSelect={() => handleSelect(tag.id)}
                  className={cn(
                    "cursor-pointer select-none rounded-lg transition-colors",
                    "hover:bg-[#439476]/10 hover:text-[#439476]",
                    value.includes(tag.id) && "bg-[#439476]/10 text-[#439476]"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div 
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-md border transition-colors",
                        value.includes(tag.id) 
                          ? "bg-[#439476] border-[#439476] text-white" 
                          : "border-[#2F2F2F]/10 dark:border-[#333840] bg-white/50 dark:bg-[#1E2B42]/50",
                        "hover:border-[#439476] dark:hover:border-[#439476]"
                      )}
                    >
                      {value.includes(tag.id) && (
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      )}
                    </div>
                    <span 
                      className="rounded-sm px-1.5 py-0.5"
                      style={{
                        backgroundColor: `${tag.color}15`,
                        color: tag.color,
                      }}
                    >
                      {tag.name}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-1.5">
        {selectedTags.map(tag => (
          <Badge
            key={tag.id}
            variant="secondary"
            className="cursor-pointer px-2 py-0.5 hover:opacity-80 transition-opacity"
            onClick={() => handleSelect(tag.id)}
            style={{
              backgroundColor: `${tag.color}15`,
              color: tag.color,
              border: `1px solid ${tag.color}30`
            }}
          >
            {tag.name}
            <span className="ml-1.5 text-xs opacity-60">×</span>
          </Badge>
        ))}
      </div>
    </div>
  )
} 